import Vue from "vue";
import App from "./App.vue";
import vuetify from "./plugins/vuetify";

import router from "./app-js/router.js";
import VueCryptojs from "vue-cryptojs";

import lang from "./app-js/lang.js";
import messages from "./app-js/messages.js";
import urls from "./app-js/urls.js";
import rules from "./app-js/rules.js";
import keys from "./app-js/keys.js";
import pre_verification_headers from "./app-js/headers/per-verification";
import business_loan_header from "./app-js/headers/business-loan.js";
import pl_funnel_headers from "./app-js/headers/pl-funnel";

import axios_instance from "./app-js/helper-axios.js";
import axios from "axios";
import VueApexCharts from "vue-apexcharts";
import LucideIcon from "./components/ui/LucideIcon.vue";

import { store } from "./store/";

Vue.prototype.$lang = lang;
Vue.prototype.$messages = messages;
Vue.prototype.$urls = urls;
Vue.prototype.$keys = keys;
Vue.prototype.$rules = rules;
Vue.prototype.$pre_verification_headers = pre_verification_headers;
Vue.prototype.$business_loan_header = business_loan_header;
Vue.prototype.$pl_funnel_headers = pl_funnel_headers;

Vue.config.productionTip = false;

Vue.use(VueCryptojs);
Vue.use(VueApexCharts);

Vue.component("apexchart", VueApexCharts);

// Registered globally: the icon set is used by almost every screen, and a
// per-file import of a 30-line wrapper is noise. Usage: <LucideIcon name="bell" />
Vue.component("LucideIcon", LucideIcon);

/**
 * Display-only cleanup of a pipeline stage name.
 *
 * The stage vocabulary is the server's ("Pending By NBFC", "Approved By NBFC",
 * ...) and the exact string is sent straight back as a filter param, so it must
 * never be rewritten in the data. This filter changes only what is READ: the
 * panel no longer surfaces lenders, so the trailing "By NBFC" is dropped and
 * the tab reads as "Pending".
 *
 * Anything else passes through untouched.
 */
Vue.filter("stageLabel", (value) =>
  String(value == null ? "" : value)
    .replace(/\s*By NBFC$/i, "")
    .trim()
);

let CancelToken = axios.CancelToken;

// Per-key cancel tokens.
//
// `isCancel` used to be a boolean sharing ONE token across the whole app, so a
// list request that cancelled its predecessor also killed every other in-flight
// request - and any call that opted out (isCancel: false) could never be
// cancelled at all, leaving superseded requests (e.g. the tab-count aggregates
// fired on every keystroke of a search) running to completion on the server.
//
// Pass a string key to give a call its own token: a new request with the same
// key cancels only the previous request with that key. `true` keeps the old
// shared-token behaviour for existing callers.
const cancelSources = {};
const SHARED_CANCEL_KEY = "__shared__";

// Shown when the API times out (axios timeout), is unreachable (network error /
// browser-reported "CORS error" on a bare gateway page) or nginx answers with a
// 502/503/504 - so the screen never just goes blank or spins forever.
const SERVER_UNREACHABLE_MESSAGE =
  "Server took too long or is unreachable. Try narrowing the search or filters.";

function nextCancelToken(isCancel) {
  if (!isCancel) return "";
  const key = isCancel === true ? SHARED_CANCEL_KEY : isCancel;
  if (cancelSources[key]) {
    cancelSources[key].cancel();
  }
  cancelSources[key] = CancelToken.source();
  return cancelSources[key].token;
}

Vue.mixin({
  beforeRouteLeave: function(to, from, next) {
    // console.log("base before route leave");
    // console.log(to, "this is to");
    // console.log(to.name.toLowerCase());
    if (to.meta.requiresAuth == true) {
      let permission_list =
        JSON.parse(sessionStorage.getItem("permission_list")) || [];
      permission_list = permission_list.join(" ").toLowerCase();
      if (permission_list.includes(to.meta.title.toLowerCase())) next();
      else next(from);
    }
    next();
  },
  methods: {
    getPermission(item) {
      let permission_list =
        JSON.parse(sessionStorage.getItem("permission_list")) || [];
      if (permission_list.includes(item)) {
        return true;
      } else {
        return false;
      }
    },
    /**
     * The locks for `screen`: [{ param, values? }].
     *
     * Read from the store, NOT from sessionStorage. That is the whole point:
     * these are refreshed from the server on every page load (see Base.vue),
     * and a template can only redraw when the thing it reads is reactive.
     * Reading sessionStorage here meant a member kept seeing City hidden after
     * an admin had removed the restriction, for the rest of their session.
     */
    screenLocks(screen) {
      const locks = this.$store.getters.getViewFilterLocks || {};
      return locks[screen] || [];
    },
    /**
     * Is `param` locked for this user on `screen`?
     *
     * A locked filter is one an admin fixed for them in Team -> Filtered view.
     * Hiding the control is cosmetic: the server applies the restriction to the
     * queryset either way, so putting the control back cannot widen what they
     * see.
     */
    filterLocked(screen, param) {
      return this.screenLocks(screen).some((lock) => lock.param === param);
    },
    /**
     * What `param` is pinned to on `screen`, or null.
     *
     * Only ever answers for the filters a screen draws as a tab bar or a count
     * strip -- those are the only ones whose values the server sends, because
     * they are the only ones the UI cannot render without. Everything else is
     * name-only and returns null here even when locked.
     */
    lockedValues(screen, param) {
      const lock = this.screenLocks(screen).find((l) => l.param === param);
      return lock && lock.values && lock.values.length ? lock.values : null;
    },
    /**
     * `items` narrowed to what a locked filter allows, for a tab bar or count
     * strip. Unlocked, or locked on something absent from `items`, hands back
     * `items` untouched -- a strip that filtered down to nothing would read as
     * a broken screen.
     */
    allowedSegments(screen, param, items, key) {
      const allowed = this.lockedValues(screen, param);
      if (!allowed) return items;
      const kept = items.filter((item) => allowed.indexOf(item[key]) !== -1);
      return kept.length ? kept : items;
    },
    showSnakeBar(type, text) {
      this.$store.dispatch("snackbar/setSnackbar", {
        flag: true,
        text,
        color: type,
      });
    },
    getApiErrorMessage(error) {
      if (!error || !error.response || !error.response.data) {
        return null;
      }
      const data = error.response.data;
      if (data.detail) {
        return data.detail;
      }
      if (data.message) {
        return data.message;
      }
      return null;
    },
    toggle_loading() {
      this.$store.dispatch("setPageLoader", !this.$store.state.page_loader);
    },
    handle401(status_code) {
      // console.log(status_code);
      if (status_code === 401) {
        // console.log("Invalid Access token");
        this.showSnakeBar("error", "Invalid Access token");
      }
    },
    handle440(status_code) {
      if (status_code === 440) {
        console.log("Session Expired");
        this.showSnakeBar("error", "Session Expired");
      }
    },
    handle404(status_code) {
      if (status_code === 404) {
        console.log("Page Not Found");
        this.showSnakeBar("error", "Page Not Found");
      }
    },
    handle403(status_code) {
      if (status_code === 403) {
        console.log("Invalid token");
        this.showSnakeBar("error", "Invalid token");
      }
    },
    /* handel 500 error*/
    handle500(status_code) {
      console.log(status_code);
      if (status_code === 500) {
        console.log("Internal server error");
        this.showSnakeBar("error", "Internal server error");
      }
    },
    /* handle 502/503/504 (gateway / timeout) errors */
    handle5xx(status_code) {
      if (status_code === 502 || status_code === 503 || status_code === 504) {
        console.log("Server unreachable or timed out", status_code);
        this.showSnakeBar("error", SERVER_UNREACHABLE_MESSAGE);
      }
    },
    /* message for a failed request: detail -> message -> 5xx -> generic */
    getRequestErrorMessage(error) {
      if (!error || !error.response) {
        return SERVER_UNREACHABLE_MESSAGE;
      }
      const status = error.response.status;
      const apiMessage = this.getApiErrorMessage(error);
      if (apiMessage) {
        return apiMessage;
      }
      if (status === 502 || status === 503 || status === 504) {
        return SERVER_UNREACHABLE_MESSAGE;
      }
      return `Something went wrong (HTTP ${status})`;
    },
    /* shared catch handling for request_GET/POST/PATCH/PUT */
    handleRequestError(error) {
      if (error.response) {
        const status = error.response.status;
        this.handle401(status);
        this.handle440(status);
        this.handle403(status);
        this.handle500(status);
        this.handle5xx(status);
        const handled = [401, 440, 403, 500, 502, 503, 504];
        if (status >= 400 && handled.indexOf(status) === -1) {
          this.showSnakeBar("error", this.getRequestErrorMessage(error));
        }
      } else {
        // network error, CORS-blocked gateway page or axios timeout (ECONNABORTED)
        this.showSnakeBar("error", SERVER_UNREACHABLE_MESSAGE);
      }
    },
    /* handel 500 error*/
    handleNoInternetConnection() {
      console.log("No Internet");
      this.showSnakeBar("error", "Please check your internet connection");
    },

    /* common  function  for GET API call */
    request_GET: (
      self,
      url,
      params,
      fnSuccessResponse = null,
      headers = null,
      fnFailureResponse = null,
      finallyHandler = null,
      isCancel = false
    ) => {
      const cancelToken = nextCancelToken(isCancel);

      if (headers == null) {
        headers = {
          token: sessionStorage.getItem("token"),
          "application-type": "web",
        };
      }

      axios_instance
        .get(url, {
          params: params,
          headers: headers,
          cancelToken: cancelToken,
        })
        .then(function(response) {
          // console.log("response")
          if (fnSuccessResponse != null) {
            if (response.data.success == false)
              self.showSnakeBar("error", response.data.message);

            fnSuccessResponse(response);
          }
        })
        .catch(function(error) {
          // console.log(error.response);
          if (axios.isCancel(error)) {
            // superseded by a newer request - no toast
            if (fnFailureResponse != null) {
              fnFailureResponse(error);
            }
            return;
          }
          console.log("request_GET catch", error);
          self.handleRequestError(error);
          if (fnFailureResponse != null) {
            fnFailureResponse(error);
          }
        })
        .finally(function(res) {
          if (finallyHandler !== null) {
            finallyHandler();
          }
        });
    },
    /* common function for the POST API Call */
    request_POST(
      self,
      url,
      params,
      fnSuccessResponse = null,
      headers = null,
      fnFailureResponse = null,
      finallyHandler = null,
      isCancel = false
    ) {
      const cancelToken = nextCancelToken(isCancel);
      if (headers == null) {
        let token = "";
        if (sessionStorage.getItem("token"))
          token = sessionStorage.getItem("token");

        headers = {
          token: token,
          "application-type": "web",
        };
      }
      axios_instance
        .post(url, params, {
          headers: headers,
          cancelToken: cancelToken,
        })
        .then(function(response) {
          // console.log("response.data.success=" + response.data.success);
          if (fnSuccessResponse != null) {
            if (response.data.success == false)
              self.showSnakeBar("error", response.data.message);

            fnSuccessResponse(response);
          }
        })
        .catch(function(error) {
          console.log(error);
          if (axios.isCancel(error)) {
            return;
          }
          self.handleRequestError(error);
          if (fnFailureResponse != null) {
            fnFailureResponse(error);
          }
        })
        .finally(function(res) {
          if (finallyHandler !== null) {
            finallyHandler();
          }
        });
    },
    /* common function for the PATCH API Call */
    request_PATCH(
      self,
      url,
      params,
      body,
      fnSuccessResponse = null,
      headers = null,
      fnFailureResponse = null,
      finallyHandler = null,
      isCancel = false
    ) {
      const cancelToken = nextCancelToken(isCancel);
      if (headers == null) {
        let token = "";
        if (sessionStorage.getItem("token"))
          token = sessionStorage.getItem("token");

        headers = {
          token: token,
          "application-type": "web",
        };
      }
      axios_instance
        .patch(url + params, body, {
          headers: headers,
          cancelToken: cancelToken,
        })
        .then(function(response) {
          // console.log("response.data.success=" + response.data.success);
          if (fnSuccessResponse != null) {
            if (response.data.success == false)
              self.showSnakeBar("error", response.data.message);

            fnSuccessResponse(response);
          }
        })
        .catch(function(error) {
          console.log(error);
          if (axios.isCancel(error)) {
            return;
          }
          self.handleRequestError(error);
          if (fnFailureResponse != null) {
            fnFailureResponse(error);
          }
        })
        .finally(function(res) {
          if (finallyHandler !== null) {
            finallyHandler();
          }
        });
    },
    request_PUT(
      self,
      url,
      look_up_key,
      body,
      fnSuccessResponse = null,
      headers = null,
      fnFailureResponse = null,
      finallyHandler = null,
      isCancel = false
    ) {
      const cancelToken = nextCancelToken(isCancel);
      if (headers == null) {
        let token = "";
        if (sessionStorage.getItem("token"))
          token = sessionStorage.getItem("token");

        headers = {
          token: token,
          "application-type": "web",
        };
      }
      axios_instance
        .put(url + look_up_key, body, {
          headers: headers,
          cancelToken: cancelToken,
        })
        .then(function(response) {
          // console.log("response.data.success=" + response.data.success);
          if (fnSuccessResponse != null) {
            if (response.data.success == false)
              self.showSnakeBar("error", response.data.message);

            fnSuccessResponse(response);
          }
        })
        .catch(function(error) {
          console.log(error);
          if (axios.isCancel(error)) {
            return;
          }
          self.handleRequestError(error);
          if (fnFailureResponse != null) {
            fnFailureResponse(error);
          }
        })
        .finally(function(res) {
          if (finallyHandler !== null) {
            finallyHandler();
          }
        });
    },
    /*perform encription for Rout params */
    encript: function(text) {
      return this.CryptoJS.AES.encrypt(text.toString(), "cashpo").toString();
    },
    /*perform decription for Rout params*/
    decrypt: function(text) {
      return this.CryptoJS.AES.decrypt(text, "cashpo").toString(
        this.CryptoJS.enc.Utf8
      );
    },

    /*convert date formte to dd-mm-yyyy*/
    display_date_formate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      let new_date = `${day}-${month}-${year}`;
      return new_date;
    },
    display_date_formate2(date) {
      if (!date) return null;
      const [day, month, year] = date.split("-");
      let new_date = `${year}-${month}-${day}`;
      return new_date;
    },

    // new axios methods

    GET_request(
      self,
      url,
      look_up_key,
      params,
      successHandler = null,
      headers = null,
      failureHandler = null,
      finallyHandler = null,
      isCancel = false
    ) {
      const cancelToken = nextCancelToken(isCancel);

      if (headers == null) {
        headers = {
          token: sessionStorage.getItem("token"),
          "application-type": "web",
        };
      }

      axios_instance
        .get(url + look_up_key, {
          params: params,
          headers: headers,
          cancelToken: cancelToken,
        })
        .then(function(response) {
          if (successHandler != null) {
            if (response.status != 200 && response.status != 201) {
              self.showSnakeBar("error", response.data.detail);
              console.log("GET then error: ", response);
            }
            successHandler(response);
          }
        })
        .catch(function(error) {
          if (!error.response) {
            if (axios.isCancel(error)) {
              return;
            }
            console.log("GET catch !error.response", error);
            self.showSnakeBar("error", SERVER_UNREACHABLE_MESSAGE);
            if (failureHandler != null) {
              failureHandler();
            }
          } else {
            self.showSnakeBar("error", self.getRequestErrorMessage(error));
            // console.log("Post catch error:", error.response.data);
            self.handle401(error.response.status);
            self.handle440(error.response.status);
            self.handle403(error.response.status);
            self.handle500(error.response.status);
            self.handle5xx(error.response.status);
            console.log("GET catch error.response:", error);
            if (failureHandler != null) {
              failureHandler();
            }
          }
        })
        .finally(function(res) {
          if (finallyHandler !== null) {
            finallyHandler();
          }
        });
    },
    POST_request(
      self,
      url,
      params,
      fnSuccessResponse = null,
      headers = null,
      failureHandler = null,
      finallyHandler = null,
      isCancel = false
    ) {
      const cancelToken = nextCancelToken(isCancel);
      if (headers == null) {
        let token = "";
        if (sessionStorage.getItem("token"))
          token = sessionStorage.getItem("token");

        headers = {
          token: token,
          "application-type": "web",
        };
      }
      axios_instance
        .post(url, params, {
          headers: headers,
          cancelToken: cancelToken,
        })
        .then(function(response) {
          if (fnSuccessResponse != null) {
            if (response.status != 200 && response.status != 201) {
              self.showSnakeBar("error", response.data.detail);
            }
            fnSuccessResponse(response);
            self.showSnakeBar("success", response.data.detail);
          }
        })
        .catch(function(error) {
          if (!error.response) {
            if (axios.isCancel(error)) {
              return;
            }
            console.log("POST catch !error.response", error);
            self.showSnakeBar("error", SERVER_UNREACHABLE_MESSAGE);
            if (failureHandler != null) {
              failureHandler();
            }
          } else {
            self.showSnakeBar("error", self.getRequestErrorMessage(error));
            // console.log("Post catch error:", error.response.data);
            console.log("POST catch error.response:", error);
            self.handle401(error.response.status);
            self.handle440(error.response.status);
            self.handle404(error.response.status);
            self.handle403(error.response.status);
            self.handle500(error.response.status);
            self.handle5xx(error.response.status);
            if (failureHandler != null) {
              failureHandler();
            }
          }
        })
        .finally(function(res) {
          if (finallyHandler !== null) {
            finallyHandler();
          }
        });
    },
    PATCH_request(
      self,
      url,
      look_up_key,
      params,
      body,
      fnSuccessResponse = null,
      headers = null,
      failureHandler = null,
      finallyHandler = null,
      isCancel = false
    ) {
      const cancelToken = nextCancelToken(isCancel);
      if (headers == null) {
        let token = "";
        if (sessionStorage.getItem("token"))
          token = sessionStorage.getItem("token");

        headers = {
          token: token,
          "application-type": "web",
        };
      }
      axios_instance
        .patch(url + (look_up_key ? `${look_up_key}/` : ""), body, {
          headers: headers,
          params: params,
          cancelToken: cancelToken,
        })
        .then(function(response) {
          if (fnSuccessResponse != null) {
            if (response.status != 200 && response.status != 201) {
              self.showSnakeBar("error", response.data.detail);
            }
            fnSuccessResponse(response);
            self.showSnakeBar("success", response.data.detail);
          }
        })
        .catch(function(error) {
          if (!error.response) {
            if (axios.isCancel(error)) {
              return;
            }
            console.log("PATCH catch !error.response", error);
            self.showSnakeBar("error", SERVER_UNREACHABLE_MESSAGE);
            if (failureHandler != null) {
              failureHandler();
            }
          } else {
            self.showSnakeBar("error", self.getRequestErrorMessage(error));
            self.handle401(error.response.status);
            self.handle440(error.response.status);
            self.handle403(error.response.status);
            self.handle500(error.response.status);
            self.handle5xx(error.response.status);
            console.log("PATCH catch error.response", error);
            if (failureHandler != null) {
              failureHandler();
            }
          }
        })
        .finally(function(res) {
          if (finallyHandler !== null) {
            finallyHandler();
          }
        });
    },
    DELETE_request(
      self,
      url,
      look_up_key,
      params,
      fnSuccessResponse = null,
      headers = null,
      failureHandler = null,
      finallyHandler = null,
      isCancel = false
    ) {
      const cancelToken = nextCancelToken(isCancel);
      if (headers == null) {
        let token = "";
        if (sessionStorage.getItem("token"))
          token = sessionStorage.getItem("token");

        headers = {
          token: token,
          "application-type": "web",
        };
      }
      axios_instance
        .delete(url + (look_up_key ? `${look_up_key}/` : ""), {
          headers: headers,
          params: params,
          cancelToken: cancelToken,
        })
        .then(function(response) {
          if (fnSuccessResponse != null) {
            if (response.status != 200 && response.status != 201) {
              self.showSnakeBar("error", response.data.detail);
              console.log("DELETE error", response.data.detail);
            }
            fnSuccessResponse(response);
            self.showSnakeBar("success", response.data.detail);
          }
        })
        .catch(function(error) {
          if (!error.response) {
            if (axios.isCancel(error)) {
              return;
            }
            console.log("DELETE catch !error.response", error);
            self.showSnakeBar("error", SERVER_UNREACHABLE_MESSAGE);
            if (failureHandler != null) {
              failureHandler();
            }
          } else {
            self.showSnakeBar("error", self.getRequestErrorMessage(error));
            self.handle401(error.response.status);
            self.handle440(error.response.status);
            self.handle403(error.response.status);
            self.handle500(error.response.status);
            self.handle5xx(error.response.status);
            console.log("DELETE catch error.response", error);
            if (failureHandler != null) {
              failureHandler();
            }
          }
        })
        .finally(function(res) {
          if (finallyHandler !== null) {
            finallyHandler();
          }
        });
    },
  },
});

const eventsHub = new Vue({
  vuetify,
  router,
  store,
  render: (h) => h(App),
}).$mount("#app");
